import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, FileText, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, rupee, pnlClass } from "@/lib/api";

const shift = (m, n) => { const [y, mo] = m.split("-").map(Number); const d = new Date(y, mo - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const label = (m) => new Date(`${m}-01T00:00:00`).toLocaleString("en-IN", { month: "long", year: "numeric" });
const HEAD = ["Strategy", "Trades", "Win %", "Gross", "Est. charges", "Net", "Max DD", "Best", "Worst"];

export const MonthlyStatement = ({ rev }) => {
  const [month, setMonth] = useState(null);
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/execution/statement", { params: { month: month || undefined } }).then((r) => { setSt(r.data); if (!month) setMonth(r.data.month); }).catch(() => {});
  }, [month, rev]);

  const email = async () => {
    setBusy(true);
    try { await api.post("/alerts/send/monthly", null, { params: { month } }); toast.success(`Statement for ${label(month)} emailed`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!month || !st) return null;
  const csv = `${process.env.REACT_APP_BACKEND_URL}/api/execution/statement.csv?month=${month}`;
  const Row = ({ r, total }) => (
    <tr className={total ? "font-semibold" : ""} data-testid={`statement-row-${r.kind}`}>
      <td className="text-slate-200">{r.label}</td><td className="num">{r.trades}</td>
      <td className="num">{r.win_rate == null ? "—" : `${r.win_rate}%`}</td>
      <td className={`num ${pnlClass(r.gross)}`}>{rupee(r.gross)}</td><td className="num text-slate-400">{rupee(r.charges)}</td>
      <td className={`num ${pnlClass(r.net)}`}>{rupee(r.net)}</td><td className="num down">{r.max_dd ? rupee(-r.max_dd) : "₹0"}</td>
      <td className={`num ${pnlClass(r.best)}`}>{rupee(r.best)}</td><td className={`num ${pnlClass(r.worst)}`}>{rupee(r.worst)}</td>
    </tr>
  );

  return (
    <div className="panel col-span-12 min-w-0 p-4" data-testid="panel-monthly-statement">
      <div className="flex flex-wrap items-center gap-2">
        <div className="eyebrow flex items-center gap-2"><FileText className="h-3.5 w-3.5" /> Monthly statement · live trades</div>
        <div className="flex w-full items-center gap-1 sm:ml-auto sm:w-auto">
          <Button data-testid="button-statement-prev" size="icon" variant="ghost" className="h-7 w-7" onClick={() => setMonth(shift(month, -1))}><ChevronLeft size={14} /></Button>
          <span className="w-28 text-center text-sm font-semibold text-slate-200 sm:w-32" data-testid="statement-month-label">{label(month)}</span>
          <Button data-testid="button-statement-next" size="icon" variant="ghost" className="h-7 w-7" onClick={() => setMonth(shift(month, 1))}><ChevronRight size={14} /></Button>
          <a href={csv} data-testid="link-statement-csv" className="ml-auto inline-flex h-7 items-center gap-1 rounded-md border border-slate-700 px-2 text-xs text-slate-300 hover:text-slate-100 sm:ml-2"><Download size={12} /> CSV</a>
          <Button data-testid="button-statement-email" size="sm" variant="outline" disabled={busy} onClick={email} className="h-7 gap-1 border-slate-700 text-xs"><Mail size={12} /> {busy ? "Sending…" : "Email"}</Button>
        </div>
      </div>
      <div className="mt-3 text-sm" data-testid="statement-net">
        Net P&amp;L <b className={`num ${pnlClass(st.total.net)}`}>{rupee(st.total.net)}</b>
        <span className="ml-2 text-xs text-slate-500">gross {rupee(st.total.gross)} · est. charges {rupee(st.total.charges)} · {st.total.trades} trades</span>
      </div>
      {st.rows.length ? (
        <div className="mt-2 overflow-x-auto">
          <table className="dense w-full text-xs" data-testid="table-statement">
            <thead><tr>{HEAD.map((h) => <th key={h} className="text-left">{h}</th>)}</tr></thead>
            <tbody>{st.rows.map((r) => <Row key={r.kind} r={r} />)}<Row r={st.total} total /></tbody>
          </table>
        </div>
      ) : <p className="mt-2 text-[11px] text-slate-500" data-testid="statement-empty">No closed live trades in {label(month)}.</p>}
      <p className="mt-2 text-[11px] text-slate-500">Charges are estimated at {rupee(st.cost_per_trade)} per trade (Settings → Costs / trade). For tax, check these against your Dhan contract notes. The statement is emailed automatically after the close on the last trading day of each month.</p>
    </div>
  );
};
